// Builds the report_data context string that rides with a field-insight request:
// the ALREADY-COMPUTED data behind the on-screen report, so the chatbot answers
// from the exact numbers the user is looking at without re-querying the lake.
//
// Strategy: find the security identifier (CUSIP-like token) in the clicked row's
// description and collect the leaf-most record objects in the report payload that
// contain it - that is the full computed record (costs, prices, dates, yields)
// for the clicked row. When no identifier matches (total rows, header fields),
// fall back to the whole payload, capped.

const LIMIT = 60000;

// Leaf-most matching objects: post-order walk; a parent whose subtree already
// produced a match is not collected itself (else a section object containing
// every row would swallow the row records).
const collectMatches = (node, token, out) => {
  if (!node || typeof node !== 'object') return false;
  let childHit = false;
  const children = Array.isArray(node) ? node : Object.values(node);
  for (const child of children) {
    childHit = collectMatches(child, token, out) || childHit;
  }
  if (childHit) return true;
  if (!Array.isArray(node)) {
    try {
      if (JSON.stringify(node).toLowerCase().includes(token)) {
        out.push(node);
        return true;
      }
    } catch (_e) {
      return false;
    }
  }
  return false;
};

// CUSIP-like identifier from the row description: an 8-9 char alphanumeric token
// containing at least one digit (all-letter words like VARIABLE are prose, and a
// CUSIP always has digits), preferring letter+digit mixes over all-digit tokens
// (which are usually dates/amounts).
export const rowIdentifierOf = (rowDescription) => {
  const tokens = String(rowDescription || '').toUpperCase().match(/\b[0-9A-Z]{8,9}\b/g) || [];
  const withDigit = tokens.filter((t) => /[0-9]/.test(t));
  const mixed = withDigit.find((t) => /[A-Z]/.test(t));
  return mixed || withDigit[0] || null;
};

// Shrink a payload to fit LIMIT while staying VALID JSON (the backend model
// reads it as ground truth): progressively cap every array to its first N
// items instead of blind-slicing the serialized string mid-token.
const capArrays = (node, cap) => {
  if (Array.isArray(node)) {
    const kept = node.slice(0, cap).map((child) => capArrays(child, cap));
    if (node.length > kept.length) kept.push(`(${node.length - kept.length} more items truncated)`);
    return kept;
  }
  if (node && typeof node === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(node)) out[k] = capArrays(v, cap);
    return out;
  }
  return node;
};

export const buildReportSlice = (activeData, header, cellCtx) => {
  const payload = { header: header || null };
  const token = rowIdentifierOf(cellCtx && cellCtx.row_description);
  if (token && activeData) {
    const rows = [];
    collectMatches(activeData, token.toLowerCase(), rows);
    if (rows.length) payload.matched_rows = rows.slice(0, 8);
  }
  if (!payload.matched_rows) payload.report = activeData || null;
  try {
    let json = JSON.stringify(payload);
    for (const cap of [50, 20, 10, 5, 2]) {
      if (json.length <= LIMIT) return json;
      json = JSON.stringify(capArrays(payload, cap));
    }
    return json.length <= LIMIT ? json : JSON.stringify({ header: payload.header || null });
  } catch (_e) {
    return null;
  }
};
